import { useState, useCallback } from "react";
import Navbar from "@/components/Navbar";
import BookingWidget from "@/components/BookingWidget";
import Fleet from "@/components/Fleet";
import Services from "@/components/Services";
import Commitments from "@/components/Commitments";
import Gallery from "@/components/Gallery";
import ReviewsCta from "@/components/ReviewsCta";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsApp";
import { useLang } from "@/i18n";
import { Clock, ShieldCheck, Check } from "lucide-react";

export default function Home() {
  const [vehicle, setVehicle] = useState("business");
  const { t } = useLang();

  const scrollToSimulator = useCallback(() => {
    document.getElementById("simulateur")?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const chooseVehicle = useCallback((id) => {
    setVehicle(id);
    scrollToSimulator();
  }, [scrollToSimulator]);

  const marquee = [1, 2, 3, 4, 5, 6].map((i) => ({ text: t(`mq.${i}`), gold: i % 2 === 0 }));

  return (
    <div data-testid="home-page">
      <Navbar />

      {/* Hero */}
      <section id="hero" className="relative pt-12 pb-20 md:pt-20 md:pb-28 px-6 border-b border-[#212631] overflow-hidden">
        <img src="/paris-hero.jpg"
          alt="Paris, Arc de Triomphe et Tour Eiffel de nuit" loading="eager"
          className="absolute inset-0 w-full h-full object-cover object-center opacity-45 pointer-events-none" data-testid="hero-bg-image" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060709]/80 via-[#060709]/75 to-[#060709] pointer-events-none"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#060709]/70 via-transparent to-[#060709]/60 pointer-events-none"></div>
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#c9a24d]/10 rounded-full blur-[120px] pointer-events-none"></div>

        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative z-10">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-[#12151e] border border-[#c9a24d]/30 text-xs font-mono text-[#c9a24d] uppercase tracking-[0.2em] mb-6" data-testid="hero-badge">
              <span className="w-2 h-2 rounded-full bg-[#c9a24d] animate-ping"></span>
              {t("hero.badge")}
            </div>

            <h1 className="font-disp text-4xl sm:text-6xl md:text-7xl font-normal tracking-tight leading-[1.02] text-white mb-6" data-testid="hero-title">
              <span className="ln"><span>{t("hero.t1")}</span></span>
              <span className="ln"><span className="italic text-[#c9a24d]">{t("hero.t2")}</span></span>
            </h1>

            <p className="fade-hero text-base md:text-lg text-[#9ea8b6] max-w-xl mb-8 font-light leading-relaxed" data-testid="hero-subtitle">
              {t("hero.sub")}
            </p>

            <div className="fade-hero grid grid-cols-2 sm:grid-cols-3 gap-4 pt-2 text-xs font-mono text-[#a2adbb]">
              <div className="flex items-center gap-2 bg-[#0d1017]/80 border border-[#212631] px-3.5 py-2.5 rounded-lg" data-testid="chip-247">
                <Clock className="w-4 h-4 text-[#c9a24d] shrink-0" />
                <span>{t("hero.c1")}</span>
              </div>
              <div className="flex items-center gap-2 bg-[#0d1017]/80 border border-[#212631] px-3.5 py-2.5 rounded-lg" data-testid="chip-payment">
                <ShieldCheck className="w-4 h-4 text-[#c9a24d] shrink-0" />
                <span>{t("hero.c2")}</span>
              </div>
              <div className="flex items-center gap-2 bg-[#0d1017]/80 border border-[#212631] px-3.5 py-2.5 rounded-lg col-span-2 sm:col-span-1" data-testid="chip-fixed-price">
                <Check className="w-4 h-4 text-[#c9a24d] shrink-0" />
                <span>{t("hero.c3")}</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 fade-calc">
            <BookingWidget vehicle={vehicle} setVehicle={setVehicle} />
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="py-4 border-b border-[#212631] bg-[#090b0f] overflow-hidden" data-testid="marquee">
        <div className="mq-track font-mono text-xs uppercase tracking-[0.25em] text-[#8c98a9]">
          {[...marquee, ...marquee].map((item, i) => (
            <span key={i} className={`mx-6 ${item.gold ? "text-[#c9a24d]" : "text-white font-medium"}`}>{item.text}</span>
          ))}
        </div>
      </div>

      <Fleet onChoose={chooseVehicle} />
      <Services onBook={scrollToSimulator} />
      <Commitments />
      <Gallery />
      <ReviewsCta onBook={scrollToSimulator} />
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
